'use strict';

declare global {
    /**
    * ISO 8601 formatted date time string
    * 'yyyy-MM-ddThh:mm:ssZ'
    */
    interface ISODateTimeString extends String { }

    /**
    * ISO 8601 formatted date time string
    * 'yyyy-MM-dd'
    */
    interface ISODateString extends String { }

    interface DateConstructor {
        /**
        * Tries to parse a Date from a string, returns 'null' on failure
        * @param date The date string to parse
        */
        parseOrNull(date: string | ISODateString | ISODateTimeString): Date;

        new (vd: ISODateString): Date;
        new (vd: ISODateTimeString): Date;

        /**
         * Compare two dates and return a negative, zero or positive value;
         * eg. negative: other is higher, zero: other is equal, positive: this is higher
         */
        compare(a: Date, b: Date): number;

        diff(a: Date, b: Date): { days: number, hours: number, minutes: number, seconds: number, milliseconds: number }

        today(): Date;

    }

    interface Date {
        /**
        * Clone this Date as a new Date object
        */
        clone(): Date;

        /**
        * Returns a new Date with n days added
        * @param days The number of days to add
        */
        addDays: (days: number) => Date;

        /**
         * Return a ISO8601 date string "yyyy-MM-dd"
         */
        toISODateString: () => ISODateString;

        /**
         * Return a ISO8601 date/time string "yyyy-MM-ddThh:mm:ddZ"
         */
        toISODateTimeString: () => ISODateTimeString;

        /**
         * Checks if the date is indeed valid
         */
        isValid(): boolean;

        /**
         * Compare this date to another date and return a negative, zero or positive value;
         * eg. negative: other is higher, zero: other is equal, positive: this is higher
         */
        compare(other: Date): number;

        /**
         * Compare this date to another date, and return if they are equal
         */
        equals(other: Date): boolean;
    }
}

Date.today = function () {
    var d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
}

Date.parseOrNull = function (date: string | ISODateString | ISODateTimeString) {
    if (date === undefined || date === null)
        return null;
    var d = new Date(date);
    return d.isValid() ? d : null;
}

Date.compare = function (a: Date, b: Date) {
    if (!a && !b)
        return 0;
    if (!a && b)
        return 1;
    if (a && !b)
        return -1;
    return a.getTime() - b.getTime();
}

Date.diff = function (a: Date, b: Date) {
    if (!a)
        throw "Date a cannot be null";
    if (!b)
        throw "Date b cannot be null";
    var am = a.getTime();
    var bm = b.getTime();
    var ms = bm - am;
    var s = Math.round(ms / 1000);
    var m = Math.round(s / 60);
    var h = Math.round(m / 60);
    var d = Math.round(h / 24);
    return {
        days: d,
        hours: h,
        minutes: m,
        seconds: s,
        milliseconds: ms
    }
}

Date.prototype.compare = function (this: Date, other: Date) {
    return Date.compare(this, other);
}

Date.prototype.equals = function (this: Date, other: Date) {
    return this.compare(other) === 0;
}

Date.prototype.clone = function (this: Date) {
    return new Date(this.getTime());
}

Date.prototype.addDays = function (this: Date, days: number) {
    var date = this.clone();
    date.setDate(date.getDate() + days);
    return date;
}

Date.prototype.toISODateString = function (this: Date) {
    return `${this.getFullYear()}-${(this.getMonth() + 101).toString().substr(1)}-${(this.getDate() + 100).toString().substr(1)}`;
}

Date.prototype.toISODateTimeString = function (this: Date) {
    return this.toISOString();
}

Date.prototype.isValid = function (this: Date) {
    return this.toString() != "Invalid Date";
}

export module DateTime {
    /**
     * Takes a time formatter string (HH:mm:ss?) and sets it on a date object;
     * If no baseDate is supplied, today is used
     * @param value time string
     * @param baseDate optional date to set time on.
     */
    export function TimeToDateTime(value: string, baseDate?: Date) {
        var m = value.split(":");
        var date = (baseDate || new Date()).clone();
        date.setHours(parseInt(m[0]));
        date.setMinutes(parseInt(m[1]));
        return date;
    }
}
